import { Box, Container, Skeleton, Stack } from '@mui/material'

export interface DetailsShimmerProps {
    variant: 'mobile' | 'desktop'
}

/** Room types shown before the fold. */
const ROOM_CARD_COUNT = 2
/** Rate rows inside each room card. */
const RATE_ROW_COUNT = 2
/** Thumbnails in a room's image strip (matches ROOM_CAROUSEL_MAX_VISIBLE). */
const ROOM_THUMB_COUNT = 5
/** Amenity chips previewed per room (matches ROOM_AMENITIES_PREVIEW_COUNT). */
const AMENITY_PREVIEW_COUNT = 4

const CardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            bgcolor: 'neutral.white',
            border: 1,
            borderColor: 'borders.default',
            borderRadius: 1.5,
            p: 2
        }}
    >
        {children}
    </Box>
)

const StarRow = () => (
    <Stack direction="row" spacing={0.5}>
        {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} variant="circular" width={13} height={13} />
        ))}
    </Stack>
)

// Desktop gallery: large hero at the start, 2×2 thumbnail grid beside it.
const GalleryDesktopSkeleton = () => (
    <Box sx={{ display: 'flex', gap: 1, height: 360 }}>
        <Skeleton variant="rectangular" sx={{ flex: 1, height: '100%', borderRadius: 1.5 }} />
        <Box
            sx={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridAutoRows: '1fr',
                gap: 1
            }}
        >
            {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} variant="rectangular" sx={{ width: '100%', height: '100%', borderRadius: 1.5 }} />
            ))}
        </Box>
    </Box>
)

// Name, stars, address, guest-rating badge and the stay dates.
const HeaderCardSkeleton = ({ isDesktop }: { isDesktop: boolean }) => (
    <CardShell>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width={isDesktop ? '55%' : '75%'} height={28} />
                <Box sx={{ mt: 0.5 }}>
                    <StarRow />
                </Box>
                <Skeleton variant="text" width={isDesktop ? '70%' : '90%'} height={16} sx={{ mt: 0.75 }} />
            </Box>
            <Skeleton variant="rounded" width={56} height={44} sx={{ flexShrink: 0 }} />
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            {Array.from({ length: 3 }, (_, index) => (
                <Box key={index} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={13} />
                    <Skeleton variant="text" width="85%" height={18} />
                </Box>
            ))}
        </Stack>
    </CardShell>
)

const TabPillsSkeleton = () => (
    <Stack direction="row" spacing={1}>
        {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} variant="rounded" width={84} height={34} sx={{ borderRadius: 999 }} />
        ))}
    </Stack>
)

// One rate option: meal + cancellation lines on the start side, price + CTA on the end.
const RateRowSkeleton = ({ isLast }: { isLast: boolean }) => (
    <Box sx={{ py: 1.5, borderBottom: isLast ? 0 : 1, borderColor: 'divider' }}>
        <Skeleton variant="text" width="30%" height={13} />
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2} sx={{ mt: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="55%" height={16} />
                <Skeleton variant="text" width="40%" height={14} />
            </Box>
            <Box sx={{ textAlign: 'end', flexShrink: 0 }}>
                <Skeleton variant="text" width={64} height={14} sx={{ ml: 'auto' }} />
                <Skeleton variant="text" width={84} height={24} sx={{ ml: 'auto' }} />
            </Box>
            <Skeleton variant="rounded" width={96} height={36} sx={{ flexShrink: 0 }} />
        </Stack>
    </Box>
)

// One room type: name + bed meta, image strip, amenity preview, then its rate rows.
const RoomGroupCardSkeleton = ({ isDesktop }: { isDesktop: boolean }) => (
    <CardShell>
        <Skeleton variant="text" width={isDesktop ? '35%' : '60%'} height={22} />
        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
            <Skeleton variant="text" width={90} height={15} />
            <Skeleton variant="text" width={60} height={15} />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, overflow: 'hidden' }}>
            {Array.from({ length: ROOM_THUMB_COUNT }, (_, index) => (
                <Skeleton
                    key={index}
                    variant="rectangular"
                    sx={{
                        width: isDesktop ? 140 : 110,
                        height: isDesktop ? 100 : 84,
                        borderRadius: 1,
                        flexShrink: 0
                    }}
                />
            ))}
        </Stack>

        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 2,
                rowGap: 0.75,
                mt: 1.5
            }}
        >
            {Array.from({ length: AMENITY_PREVIEW_COUNT }, (_, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="circular" width={16} height={16} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="text" width={`${60 + ((index * 13) % 30)}%`} height={14} />
                </Stack>
            ))}
        </Box>
        <Skeleton variant="text" width={120} height={14} sx={{ mt: 0.75 }} />

        <Box sx={{ mt: 1 }}>
            {Array.from({ length: RATE_ROW_COUNT }, (_, index) => (
                <RateRowSkeleton key={index} isLast={index === RATE_ROW_COUNT - 1} />
            ))}
        </Box>
    </CardShell>
)

// Skeleton placeholder for the hotel details/review page while selectHotel loads.
// Mirrors the real layout section-for-section so the transition is visually stable.
const DetailsShimmer: React.FC<DetailsShimmerProps> = ({ variant }) => {
    if (variant === 'mobile') {
        return (
            <Box sx={{ minHeight: '100dvh', bgcolor: 'neutral.100' }}>
                <Skeleton variant="rectangular" height={280} />
                <Stack spacing={1.5} sx={{ p: 2 }}>
                    <HeaderCardSkeleton isDesktop={false} />
                    <TabPillsSkeleton />
                    {Array.from({ length: ROOM_CARD_COUNT }, (_, index) => (
                        <RoomGroupCardSkeleton key={index} isDesktop={false} />
                    ))}
                </Stack>
            </Box>
        )
    }

    return (
        <Box sx={{ bgcolor: 'neutral.100', minHeight: 'calc(100vh - 54px)' }}>
            <Container maxWidth="xl" sx={{ px: 2, py: 3 }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <GalleryDesktopSkeleton />
                        <HeaderCardSkeleton isDesktop />
                        <TabPillsSkeleton />
                        {Array.from({ length: ROOM_CARD_COUNT }, (_, index) => (
                            <RoomGroupCardSkeleton key={index} isDesktop />
                        ))}
                    </Stack>

                    {/* sticky booking rail: name, stars, "from" price, select-room CTA */}
                    <Box sx={{ width: 320, flexShrink: 0 }}>
                        <CardShell>
                            <Skeleton variant="text" width="80%" height={24} />
                            <Box sx={{ mt: 0.5 }}>
                                <StarRow />
                            </Box>
                            <Skeleton variant="text" width="40%" height={14} sx={{ mt: 1.5 }} />
                            <Skeleton variant="text" width="60%" height={30} />
                            <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
                        </CardShell>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { DetailsShimmer }
export default DetailsShimmer
